import httpClient, { getErrorMessage } from './httpClient';

// =====================================
// 📝 APPLICATION API TYPES
// =====================================

export interface SubmitApplicationRequest {
    firstName: string;
    lastName: string;
    email: string;
    linkedInUrl?: string;
    resumeFile?: File;
}

export interface SubmitApplicationResponse {
    success: boolean;
    message: string;
    application_id: string;
}

export interface VerifyOTPRequest {
    otp: string;
}

export interface VerifyOTPResponse {
    success: boolean;
    message: string;
}

export interface ResendOTPResponse {
    success: boolean;
    message: string;
}

// =====================================
// 🔧 APPLICATION API SERVICE
// =====================================

class ApplicationApiService {
    /**
     * Submit application with resume or LinkedIn URL
     */
    async submitApplication(data: SubmitApplicationRequest): Promise<SubmitApplicationResponse> {
        try {
            // Create FormData for multipart/form-data submission
            const formData = new FormData();
            formData.append('firstName', data.firstName);
            formData.append('lastName', data.lastName);
            formData.append('email', data.email);

            if (data.linkedInUrl) {
                formData.append('linkedInUrl', data.linkedInUrl);
            }

            if (data.resumeFile) {
                formData.append('resume', data.resumeFile);
            }

            const response = await httpClient.post<{ payload: SubmitApplicationResponse }>(
                '/application/v1/submit',
                formData
                // Axios automatically sets Content-Type with boundary for FormData
            );

            return response.data.payload;
        } catch (error) {
            throw new Error(getErrorMessage(error));
        }
    }

    /**
     * Verify OTP for application
     */
    async verifyOTP(applicationId: string, otp: string): Promise<VerifyOTPResponse> {
        try {
            const response = await httpClient.post<{ payload: VerifyOTPResponse }>(
                `/application/v1/${applicationId}/otp/verify`,
                { otp }
            );

            return response.data.payload;
        } catch (error) {
            throw new Error(getErrorMessage(error));
        }
    }

    /**
     * Resend OTP for application
     */
    async resendOTP(applicationId: string): Promise<ResendOTPResponse> {
        try {
            const response = await httpClient.post<{ payload: ResendOTPResponse }>(
                `/application/v1/${applicationId}/otp/resend`
            );

            return response.data.payload;
        } catch (error) {
            throw new Error(getErrorMessage(error));
        }
    }

    /**
     * Upload resume to verified application (optional - if user didn't upload during initial submission)
     */
    async uploadResume(applicationId: string, resumeFile: File): Promise<{ success: boolean; message: string; resume_url: string }> {
        try {
            const formData = new FormData();
            formData.append('resume', resumeFile);

            const response = await httpClient.post<{ payload: { success: boolean; message: string; resume_url: string } }>(
                `/application/v1/${applicationId}/resume/upload`,
                formData
                // Axios automatically sets Content-Type with boundary for FormData
            );

            return response.data.payload;
        } catch (error) {
            throw new Error(getErrorMessage(error));
        }
    }
}

// Export singleton instance
export const applicationApiService = new ApplicationApiService();
